import { useMemo, useState } from 'react';
import { api, ApiError, num } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { AirspaceZone, AirspaceZoneType, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const AIRSPACE_OFFICER_ROLES = ['AIRSPACE_OFFICER'];
const ZONE_TYPES: AirspaceZoneType[] = ['NO_FLY', 'RESTRICTED', 'CONTROLLED', 'DANGER'];

/** M23 — SCAA's own no-fly/restricted/controlled/danger airspace reference data; backs the operation-apply intersect check. */
export function AirspaceZones() {
  const { persona } = useAuth();
  const isAirspaceOfficer = persona ? persona.roles.some((r) => AIRSPACE_OFFICER_ROLES.includes(r)) : false;
  const [zoneType, setZoneType] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '100' });
    if (zoneType) params.set('zoneType', zoneType);
    if (includeInactive) params.set('status', 'ALL');
    return `/api/v1/m23/airspace-zones?${params.toString()}`;
  }, [zoneType, includeInactive]);

  const listQ = useApiQuery<Page<AirspaceZone>>(path);

  return (
    <div>
      <PageHeader
        title="Airspace Zones"
        subtitle="Live no-fly / restricted / controlled / danger zone reference data — backs the operation-apply intersect check (M23 — SCAA)."
        actions={
          <>
            <select className="field-input !w-auto" value={zoneType} onChange={(e) => setZoneType(e.target.value)}>
              <option value="">All types</option>
              {ZONE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-1.5 text-xs text-slate-500">
              <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
              Include inactive
            </label>
            {isAirspaceOfficer ? (
              <button className="btn-primary" onClick={() => setShowCreate(true)}>
                + New zone
              </button>
            ) : null}
          </>
        }
      />

      {!isAirspaceOfficer ? <RoleNotice roles={AIRSPACE_OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No airspace zones match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Code</th>
                <th className="table-head">Name</th>
                <th className="table-head">Type</th>
                <th className="table-head">Radius</th>
                <th className="table-head">Active</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((z) => (
                <tr key={z.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedCode(z.code)}>
                  <td className="table-cell font-mono text-xs font-semibold">{z.code}</td>
                  <td className="table-cell">{z.name}</td>
                  <td className="table-cell text-xs text-slate-500">{z.zoneType.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs text-slate-500">{num(z.radiusMetres)} m</td>
                  <td className="table-cell">
                    <StatusBadge status={z.active ? 'ACTIVE' : 'EXPIRED'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateZoneModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}

      {selectedCode ? (
        <ZoneDetailModal
          code={selectedCode}
          canAct={isAirspaceOfficer}
          onClose={() => setSelectedCode(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

function CreateZoneModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [zoneType, setZoneType] = useState<AirspaceZoneType>('NO_FLY');
  const [lng, setLng] = useState('55.45');
  const [lat, setLat] = useState('-4.62');
  const [radiusMetres, setRadiusMetres] = useState('5000');
  const [ceilingM, setCeilingM] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m23/airspace-zones', {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        zoneType,
        centerPoint: { type: 'Point', coordinates: [Number.parseFloat(lng), Number.parseFloat(lat)] },
        radiusMetres: Number.parseFloat(radiusMetres),
        ...(ceilingM ? { ceilingM: Number.parseFloat(ceilingM) } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create zone');
    } finally {
      setBusy(false);
    }
  };

  const valid = code.trim().length >= 2 && name.trim().length >= 2 && radiusMetres;

  return (
    <Modal title="Create an airspace zone" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Code">
          <input className="field-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="SEZ_INTL_AIRPORT" />
        </Field>
        <Field label="Name" className="col-span-2">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Seychelles International Airport" />
        </Field>
        <Field label="Zone type">
          <select className="field-input" value={zoneType} onChange={(e) => setZoneType(e.target.value as AirspaceZoneType)}>
            {ZONE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Radius (m)">
          <input className="field-input" type="number" value={radiusMetres} onChange={(e) => setRadiusMetres(e.target.value)} />
        </Field>
        <Field label="Ceiling (m AGL, optional — blank = all altitudes)">
          <input className="field-input" type="number" value={ceilingM} onChange={(e) => setCeilingM(e.target.value)} />
        </Field>
        <Field label="Centre longitude">
          <input className="field-input" type="number" step="0.0001" value={lng} onChange={(e) => setLng(e.target.value)} />
        </Field>
        <Field label="Centre latitude">
          <input className="field-input" type="number" step="0.0001" value={lat} onChange={(e) => setLat(e.target.value)} />
        </Field>
        <Field label="Description (optional)" className="col-span-2 sm:col-span-3">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary mt-4" disabled={busy || !valid} onClick={submit}>
        {busy ? 'Creating…' : 'Create zone'}
      </button>
    </Modal>
  );
}

function ZoneDetailModal({ code, canAct, onClose, onChanged }: { code: string; canAct: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<AirspaceZone>(`/api/v1/m23/airspace-zones/${encodeURIComponent(code)}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const z = q.data;

  return (
    <Modal title={z ? `${z.code} — ${z.name}` : 'Airspace zone'} onClose={onClose}>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {z ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="Type" value={z.zoneType.replaceAll('_', ' ')} />
            <Detail label="Status" value={<StatusBadge status={z.active ? 'ACTIVE' : 'EXPIRED'} />} />
            <Detail label="Radius" value={`${num(z.radiusMetres)} m`} />
            <Detail label="Ceiling" value={z.ceilingM ? `${num(z.ceilingM)} m AGL` : 'All altitudes'} />
            <Detail label="Centre" value={`${z.centerPoint.coordinates[1]}, ${z.centerPoint.coordinates[0]}`} mono />
          </div>
          {z.description ? <Detail label="Description" value={z.description} /> : null}

          {canAct ? (
            z.active ? (
              <button className="btn-danger" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m23/airspace-zones/${z.id}/deactivate`))}>
                {busy ? 'Deactivating…' : 'Deactivate zone'}
              </button>
            ) : (
              <button className="btn-success" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m23/airspace-zones/${z.id}/reactivate`))}>
                {busy ? 'Reactivating…' : 'Reactivate zone'}
              </button>
            )
          ) : (
            <RoleNotice roles={AIRSPACE_OFFICER_ROLES} />
          )}
        </div>
      ) : null}
    </Modal>
  );
}
